import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  useMediaQuery,
  Fade,
  Slide,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useHistory } from "react-router-dom";

// MUI Icons
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import ExploreIcon from "@mui/icons-material/Explore";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/* ======================
   BACKGROUND CONSTANTS
   (Tránh lỗi TS2590)
====================== */
const HERO_BG =
  "radial-gradient(1200px 500px at 15% 20%, rgba(99,102,241,0.25), transparent 60%), " +
  "radial-gradient(900px 500px at 85% 30%, rgba(59,130,246,0.22), transparent 55%), " +
  "linear-gradient(135deg,#0b1026 0%,#141a3a 45%,#0b1026 100%)";

const HERO_OVERLAY_BG =
  "radial-gradient(circle at 20% 80%, rgba(236,72,153,0.35), transparent 45%), " +
  "radial-gradient(circle at 80% 70%, rgba(34,197,94,0.28), transparent 40%)";

/* ======================
   HOOK: REVEAL SCROLL
====================== */
function useInViewOnce(threshold = 0.18) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return { ref, inView };
}

/* ======================
   SECTION ANIMATION
====================== */
function SectionReveal({
  children,
  variant = "fade",
  timeout = 650,
  threshold = 0.18,
}: {
  children: React.ReactNode;
  variant?: "fade" | "slideUp";
  timeout?: number;
  threshold?: number;
}) {
  const { ref, inView } = useInViewOnce(threshold);

  if (variant === "slideUp") {
    return (
      <div ref={ref}>
        <Slide direction="up" in={inView} timeout={timeout} mountOnEnter>
          <div>{children}</div>
        </Slide>
      </div>
    );
  }

  return (
    <div ref={ref}>
      <Fade in={inView} timeout={timeout}>
        <div>{children}</div>
      </Fade>
    </div>
  );
}

/* =============================
        MAIN PAGE
============================= */
export default function AboutLumoraPage() {
  const flowRef = useRef<HTMLDivElement | null>(null);

  const scrollToFlow = () => {
    flowRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <Box sx={{ bgcolor: "#f7f7ff", minHeight: "100vh" }}>
      {/* HERO SECTION */}
      <Fade in timeout={600}>
        <div>
          <HeroSection onScrollToFlow={scrollToFlow} />
        </div>
      </Fade>

      {/* BENEFITS SECTION */}
      <BenefitsSection />

      {/* FLOW SECTION */}
      <div ref={flowRef}>
        <FlowSection />
      </div>

      {/* MODULES SECTION */}
      <ModulesSection />

      {/* CTA SECTION */}
      <CTASection />

      {/* FOOTER */}
      <LumoraFooter />
    </Box>
  );
}

/* -----------------------------
        HERO SECTION
----------------------------- */
function HeroSection({ onScrollToFlow }: { onScrollToFlow: () => void }) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const history = useHistory();

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 7, md: 10 },
        background: HERO_BG,
        color: "white",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.25,
          background: HERO_OVERLAY_BG,
          filter: "blur(10px)",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack spacing={2.5}>
              <Chip
                icon={<StarRoundedIcon />}
                label="Vũ trụ AI giáo dục toàn diện"
                sx={{
                  width: "fit-content",
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "white",
                  border: "1px solid rgba(255,255,255,0.18)",
                  "& .MuiChip-icon": { color: "white" },
                }}
              />

              <Typography
                variant={isMdUp ? "h2" : "h3"}
                sx={{ fontWeight: 800, letterSpacing: -0.5, lineHeight: 1.05 }}
              >
                Lumora
              </Typography>

              <Typography
                variant="h6"
                sx={{ color: "rgba(255,255,255,0.82)", maxWidth: 620 }}
              >
                Hiểu năng lực học tập • Chăm sóc cảm xúc • Định hướng tương lai —
                tất cả được kết nối trong một nền tảng dành riêng cho học sinh THPT.
              </Typography>

              <Typography
                sx={{ color: "rgba(255,255,255,0.72)", maxWidth: 640 }}
              >
                Lumora giúp bạn nhìn rõ hơn về chính mình thông qua dữ liệu học tập,
                cảm xúc và tương tác hằng ngày — từ đó học chủ động hơn, nhẹ nhàng
                hơn và tự tin bứt phá.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<RocketLaunchIcon />}
                  onClick={() => history.push("/dashboard")}
                  sx={{
                    bgcolor: "#6366f1",
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "#4f46e5" },
                  }}
                >
                  Khám phá Lumora
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={onScrollToFlow}
                  sx={{
                    borderColor: "rgba(255,255,255,0.35)",
                    color: "white",
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: "rgba(255,255,255,0.08)",
                    },
                  }}
                >
                  Xem cách hoạt động
                </Button>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <MockupCard />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

/* -----------------------------
        MOCKUP CARD
----------------------------- */
function MockupCard() {
  return (
    <Card
      sx={{
        borderRadius: 4,
        bgcolor: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(10px)",
        boxShadow: "0 18px 60px rgba(0,0,0,0.25)",
        color: "text.primary",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography sx={{ fontWeight: 800, mb: 1.5, fontSize: 18 }}>
          Snapshot cá nhân hóa
        </Typography>

        <Stack spacing={1.25}>
          <MiniStat
            label="Học lực (VIREYA)"
            value="Phân tích điểm mạnh & chiến lược môn học"
          />
          <MiniStat
            label="Cảm xúc (ZENORA)"
            value="Ghi nhận tâm trạng & hỗ trợ giải tỏa"
          />
          <MiniStat
            label="Định hướng (NEOVANA)"
            value="Gợi ý ngành nghề & lộ trình phát triển"
          />
          <MiniStat
            label="Trợ lý (ROBOKI)"
            value="Đồng hành giải đáp kiến thức 24/7"
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            height: 160,
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(79,70,229,0.35)",
          }}
        >
          <img
            src="/media/avatars/lumora.png"
            alt="Lumora Ecosystem"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        bgcolor: "rgba(99,102,241,0.06)",
        border: "1px solid rgba(99,102,241,0.15)",
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1e1b4b" }}>
        {value}
      </Typography>
    </Box>
  );
}

/* -----------------------------
        BENEFITS SECTION
----------------------------- */
function BenefitsSection() {
  const benefits = [
    {
      icon: <AutoGraphIcon />,
      title: "Hiểu năng lực học tập",
      desc: "VIREYA phân tích dữ liệu học tập để nhận diện điểm mạnh, điểm yếu và đưa ra chiến lược cải thiện tối ưu.",
    },
    {
      icon: <ExploreIcon />,
      title: "Định hướng tương lai",
      desc: "NEOVANA kết nối năng lực cá nhân với xu hướng nghề nghiệp và xây dựng lộ trình học tập cá nhân hóa.",
    },
    {
      icon: <FavoriteIcon />,
      title: "Cân bằng cảm xúc",
      desc: "ZENORA đồng hành trong việc giải tỏa áp lực, quản lý cảm xúc và nâng cao sức khỏe tinh thần.",
    },
    {
      icon: <SmartToyIcon />,
      title: "Trợ lý AI thông minh",
      desc: "ROBOKI hỗ trợ hỏi đáp 24/7, giải thích khái niệm phức tạp và gợi ý tài liệu học tập chính xác.",
    },
  ];

  return (
    <SectionReveal>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="h4" sx={{ fontWeight: 900, textAlign: "center" }}>
          Lumora giúp bạn những gì?
        </Typography>

        <Typography
          sx={{
            color: "text.secondary",
            maxWidth: 760,
            mt: 1,
            mb: 4,
            textAlign: "center",
            mx: "auto",
          }}
        >
          Một hệ sinh thái AI toàn diện hỗ trợ học sinh khám phá bản thân, làm chủ kiến thức và làm chủ tương lai.
        </Typography>

        <Grid container spacing={2.5}>
          {benefits.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.title}>
              <BenefitCard {...item} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </SectionReveal>
  );
}

function BenefitCard({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 4,
        transition: "0.3s",
        "&:hover": { transform: "translateY(-5px)" },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            display: "grid",
            placeItems: "center",
            borderRadius: 2.5,
            bgcolor: "rgba(99,102,241,.12)",
            color: "#4f46e5",
          }}
        >
          {icon}
        </Box>

        <Typography sx={{ fontWeight: 800, mt: 2, fontSize: 18 }}>
          {title}
        </Typography>

        <Typography variant="body2" sx={{ color: "text.secondary", mt: 1, lineHeight: 1.6 }}>
          {desc}
        </Typography>
      </CardContent>
    </Card>
  );
}

/* -----------------------------
        FLOW SECTION
----------------------------- */
function FlowSection() {
  const steps = [
    {
      title: "1. Thu thập & Tổng hợp",
      desc: "Dữ liệu học tập, xu hướng cảm xúc và định hướng cá nhân được tổng hợp để tạo nên hồ sơ học sinh toàn diện.",
      bullets: ["Điểm số", "Tâm trạng", "Mục tiêu"],
    },
    {
      title: "2. Phân tích chuyên sâu AI",
      desc: "Thuật toán AI phân tích để tìm ra điểm mạnh, điểm yếu và các mẫu hành vi hỗ trợ học tập.",
      bullets: ["Nhận diện xu hướng", "Tối ưu phương pháp", "Cá nhân hóa"],
    },
    {
      title: "3. Đồng hành 4 Module",
      desc: "VIREYA, NEOVANA, ZENORA và ROBOKI phối hợp cung cấp giải pháp toàn diện cho từng khía cạnh.",
      bullets: ["VIREYA", "NEOVANA", "ZENORA", "ROBOKI"],
    },
    {
      title: "4. Theo dõi & Cải thiện",
      desc: "Nền tảng liên tục cập nhật tiến trình và điều chỉnh lộ trình phát triển theo từng giai đoạn.",
      bullets: ["Đánh giá định kỳ", "Tối ưu lộ trình", "Trưởng thành"],
    },
  ];

  return (
    <SectionReveal variant="slideUp">
      <Box sx={{ py: 8, bgcolor: "white" }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 900, textAlign: "center" }}>
            Hành trình Lumora đồng hành cùng bạn
          </Typography>

          <Grid container spacing={2.5} sx={{ mt: 3 }}>
            {steps.map((s, index) => (
              <Grid item xs={12} md={6} key={s.title}>
                <Card sx={{ borderRadius: 4, height: "100%", border: "1px solid #f0f0f0" }}>
                  <CardContent sx={{ p: 3 }}>
                    <Chip
                      label={`Bước 0${index + 1}`}
                      sx={{ mb: 2, fontWeight: 800, bgcolor: "#6366f1", color: "white" }}
                    />

                    <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                      {s.title}
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 1, color: "text.secondary", lineHeight: 1.6 }}>
                      {s.desc}
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mt: 2.5, flexWrap: "wrap", gap: 0.5 }}>
                      {s.bullets.map((b) => (
                        <Chip key={b} label={b} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </SectionReveal>
  );
}

/* =============================
      MODULES SECTION
============================= */
function ModulesSection() {
  const modules = [
    {
      name: "VIREYA",
      subtitle: "Learning Intelligence",
      color: "#6366f1",
      icon: <SchoolIcon />,
      desc: "Phân tích kết quả học tập, nhận diện điểm mạnh/yếu và cá nhân hóa lộ trình ôn luyện.",
      features: [
        "Phân tích điểm mạnh & yếu theo môn học",
        "Gợi ý chiến lược học tập cá nhân",
        "Theo dõi tiến độ học tập chi tiết",
        "Tối ưu hóa thời gian ôn thi",
      ],
    },
    {
      name: "NEOVANA",
      subtitle: "Career Navigation",
      color: "#ec4899",
      icon: <ExploreIcon />,
      desc: "Định hướng nghề nghiệp tương lai dựa trên tính cách, năng lực và mục tiêu cá nhân.",
      features: [
        "Trắc nghiệm tính cách & năng lực",
        "Gợi ý ngành học & trường Đại học phù hợp",
        "Xây dựng lộ trình phát triển bản thân",
        "Cập nhật xu hướng thị trường lao động",
      ],
    },
    {
      name: "ZENORA",
      subtitle: "Emotional Wellness",
      color: "#14b8a6",
      icon: <FavoriteIcon />,
      desc: "Theo dõi cảm xúc, giải tỏa căng thẳng và chăm sóc sức khỏe tinh thần cho học sinh.",
      features: [
        "Ghi nhận & phân tích trạng thái cảm xúc",
        "Bài tập giải tỏa áp lực & căng thẳng",
        "Phát triển trí tuệ cảm xúc (EQ)",
        "Tạo môi trường học tập tích cực",
      ],
    },
    {
      name: "ROBOKI",
      subtitle: "AI Learning Assistant",
      color: "#f59e0b",
      icon: <SmartToyIcon />,
      desc: "Trợ lý AI tương tác trực tiếp, hỗ trợ giải đáp thắc mắc và tìm kiếm tài liệu tức thì.",
      features: [
        "Hỏi đáp kiến thức đa môn học 24/7",
        "Giải thích chi tiết các dạng bài tập",
        "Tóm tắt tài liệu & bài giảng nhanh chóng",
        "Gợi ý tài liệu tham khảo chất lượng",
      ],
    },
  ];

  return (
    <SectionReveal>
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, textAlign: "center" }}>
          Bốn hệ sinh thái nòng cốt của Lumora
        </Typography>

        <Typography
          sx={{ color: "text.secondary", mt: 1, mb: 4, textAlign: "center" }}
        >
          Mỗi module đảm nhiệm một vai trò riêng biệt nhưng được tích hợp hoàn hảo để phát triển con người toàn diện.
        </Typography>

        <Grid container spacing={3}>
          {modules.map((m) => (
            <Grid item xs={12} md={6} key={m.name}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 5,
                  overflow: "hidden",
                  transition: "0.35s",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 25px 50px rgba(0,0,0,.08)",
                  },
                }}
              >
                <Box sx={{ height: 6, background: m.color }} />

                <CardContent sx={{ p: 3.5 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 3,
                        display: "grid",
                        placeItems: "center",
                        background: `${m.color}15`,
                        color: m.color,
                      }}
                    >
                      {m.icon}
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 22, fontWeight: 900, lineHeight: 1.2 }}>
                        {m.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: m.color, fontWeight: 700 }}>
                        {m.subtitle}
                      </Typography>
                    </Box>
                  </Stack>

                  <Typography sx={{ mt: 2, color: "text.secondary", fontSize: 14, minHeight: 42 }}>
                    {m.desc}
                  </Typography>

                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {m.features.map((f) => (
                      <Box key={f} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CheckCircleIcon fontSize="small" sx={{ color: m.color }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {f}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </SectionReveal>
  );
}

/* =============================
          CTA SECTION
============================= */
function CTASection() {
  const history = useHistory();

  return (
    <Box
      sx={{
        py: 10,
        background: "linear-gradient(135deg,#4f46e5,#9333ea)",
        color: "white",
      }}
    >
      <Container maxWidth="md">
        <Stack alignItems="center" textAlign="center" spacing={3}>
          <Typography variant="h3" sx={{ fontWeight: 900 }}>
            Bắt đầu hành trình cùng Lumora
          </Typography>

          <Typography sx={{ opacity: 0.9, maxWidth: 650, fontSize: 16 }}>
            Không chỉ là một nền tảng học tập. Lumora là người bạn đồng hành AI giúp bạn hiểu bản thân, phát triển tiềm năng và xây dựng tương lai vững chắc.
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={() => history.push("/dashboard")}
            sx={{
              px: 5,
              py: 1.5,
              borderRadius: 99,
              background: "#fff",
              color: "#4f46e5",
              fontWeight: 800,
              textTransform: "none",
              fontSize: 16,
              "&:hover": { background: "#f5f5f5" },
            }}
          >
            Trải nghiệm ngay
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}

/* =============================
          FOOTER
============================= */
function LumoraFooter() {
  return (
    <Box sx={{ py: 6, background: "#0f172a", color: "white" }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Typography sx={{ fontSize: 24, fontWeight: 900 }}>
              LUMORA
            </Typography>
            <Typography sx={{ mt: 1, opacity: 0.7, fontSize: 14 }}>
              Hệ sinh thái AI hỗ trợ học tập, định hướng và chăm sóc cảm xúc cho học sinh THPT.
            </Typography>
          </Grid>

          <Grid item xs={6} md={4}>
            <Typography fontWeight={800} sx={{ mb: 1.5 }}>
              Hệ sinh thái
            </Typography>
            <Stack spacing={1} sx={{ opacity: 0.8, fontSize: 14 }}>
              <Typography variant="body2">VIREYA - Learning Intelligence</Typography>
              <Typography variant="body2">NEOVANA - Career Navigation</Typography>
              <Typography variant="body2">ZENORA - Emotional Wellness</Typography>
              <Typography variant="body2">ROBOKI - AI Assistant</Typography>
            </Stack>
          </Grid>

          <Grid item xs={6} md={4}>
            <Typography fontWeight={800} sx={{ mb: 1.5 }}>
              Liên hệ
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Email: contact@lumora.ai
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
              Hỗ trợ: support@lumora.ai
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,.15)" }} />

        <Typography textAlign="center" variant="body2" sx={{ opacity: 0.6 }}>
          © {new Date().getFullYear()} Lumora Ecosystem. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}